import { FC, useEffect, useState, forwardRef, useImperativeHandle, useMemo, memo } from 'react';
import { useFormik } from 'formik';

import {
  ContactType,
  ProfilePhoneEmailEnum,
  OwnerType,
  Citizenship,
  ProfileInformationModeEnum,
  updateProfileInformationData,
  updateAccountInformation,
  type AddressContactType,
  type DynamicObjectInterface,
  type ProfileInformationType,
  type AlternativeContactType,
  type DropDownData as Data,
} from '@commerce-profile-webcomponents/sdk';
import {
  Checkbox,
  Input,
  DropDown,
  DatePicker,
  TextLink,
  TextLinkStyle,
  Theme,
  IconStyle,
  FAIcon,
  InputValidations,
  TextLinkUnderlineStyle,
  CalendarYearDropdownType,
  Notification,
  useDeviceTypeMediaQuery,
} from '@nextgen-web-framework/all';
import { useSelector } from 'react-redux';
import { Logger, type AppState } from '@nextgen-web-framework/core';
import { OtpVerification } from '@commerce-identity-webcomponents/ui';
import { ProfileInitialValue, AlternativeContactInitialValue, AddressInitialValue } from '../../../utils/Utils';
import { ProfilePhoneProcessType, ProfileEmailProcessType, MyAccountInput } from '../../../utils/types/propTypes';
import { MyAccountExtEnum, MyAccountFieldType, ToastMessageEnum, MyAccountFieldName } from '../../../utils/types/enum';
import {
  processAltContact,
  handleVerifyContactCTA,
  processEmailField,
  processPhoneField,
} from '../../../utils/myAccountContactUtils';
import {
  generateDropdownListByOptions,
  getDropdownValueByOptions,
  conditionalError,
  processDateField,
  ProfileSectionConst,
  profileValidationSchema,
  profileValidation,
  isFieldTouched,
  fieldTypeByInput,
  getExt,
  handleAddressCallback,
  SliceConfigName,
} from '../../../utils/myAccountProfileUtils';
import {
  isAddressModified,
  copyAddress,
  useAddressFormat,
  getAddressConfig,
  filterFieldNames,
} from '../../../utils/myAccountAddressUtils';
import { useMyAccount } from '../../../utils/myAccountHook';
import { ifThenElse, siteCountry, parseArray, parseString, parseNumber, processLabelText } from '../../../utils/helper';
import InputErrorComponent from '../../Common/InputErrorComponent/InputErrorComponent';
import { inputErrorMessage, inputErrorStatus } from '../../Common/InputErrorComponent/utils';
import AddressInput from '../../AddressInput/AddressInput';
import PhoneNumberInput from '../../Common/PhoneNumberInput/PhoneNumberInput';
import ApiErrorModal from '../../ApiErrorModal/ApiErrorModal';
import { DisplayField } from '../../Common/DisplayField/DisplayField';
import { ApiErrorProps } from '../types/propTypes';
import { ErrorConstants } from '../../../utils/Constants';
import AlternativeContact from './AlternativeContacts/AlternativeContact';
import type { AccountInformationEditProps, AlternativeContactCallbackProps } from './types/propTypes';
import styles from './AccountInformationEdit.module.scss';

const OTPComponent = memo(OtpVerification);
const AccountInformationEdit: FC<AccountInformationEditProps> = forwardRef<
  AccountInformationEditProps,
  DynamicObjectInterface
>(
  (
    {
      saveDisabledCallback,
      setEditingMode,
      masterDataConfig,
      sliceName,
      profileId,
      accountNumber,
    }: AccountInformationEditProps,
    ref,
  ) => {
    const logger = Logger.getInstance();

    const {
      t,
      breakpoint,
      accountType,
      getDateLocale,
      defaultCountryDialCode,
      getLabel,
      addressConfig,
      isEmailRequired,
      saveProfileDataHandler,
      isAdditionalPhoneEnabled,
      addToast,
      setConfig,
      usingAccountApiForContacts,
    } = useMyAccount();
    const { isMobile } = useDeviceTypeMediaQuery();
    const addrFormat = useAddressFormat;

    // Get data from slice
    const { [sliceName]: profileInformation } = useSelector((state: AppState) => state.commerceProfile);
    const otpData = useSelector((state: AppState) => state.commerceIdentity.otpData);
    const { profileData: originalProfile } = profileInformation;
    const { isVerified: isOTPVerified } = otpData;

    const dateLocale = getDateLocale(profileInformation?.profileData);

    const siteCountryCode = siteCountry().toLowerCase();

    // Masterdata config
    const useAccountApiForContacts = usingAccountApiForContacts();
    const { inputs: configList, ext } = masterDataConfig;
    const validationOnMount = getExt(MyAccountExtEnum.VALIDATION_ON_MOUNT, ext) as boolean;

    const infoSectionConfig = configList.find(
      (section: MyAccountInput) => section.section === ProfileSectionConst.PROFILE_SECTION_INFO,
    );

    // to check visible for edit, martial is false now.
    const contactSectionConfig = configList.find(
      (section: MyAccountInput) => section.section === ProfileSectionConst.PROFILE_SECTION_CONTACT,
    );
    const addrSectionConfig = configList.find(
      (section: MyAccountInput) => section.section === ProfileSectionConst.PROFILE_SECTION_ADDRESS,
    );
    const useAsBillingConfig = addrSectionConfig?.inputs?.find(
      (input: MyAccountInput) => input.fieldName === MyAccountFieldName.USE_AS_BILLING,
    );
    const useAsMailingConfig = addrSectionConfig?.inputs?.find(
      (input: MyAccountInput) => input.fieldName === MyAccountFieldName.USE_AS_MAILING,
    );

    const addressFields = filterFieldNames(getAddressConfig(addressConfig, ContactType.Billing)?.inputs);
    const addressMandatoryFields = filterFieldNames(getAddressConfig(addressConfig, ContactType.Billing)?.inputs, true);

    // Setup validations schma
    const validationSchema = profileValidationSchema(
      [...parseArray(infoSectionConfig?.inputs), ...parseArray(contactSectionConfig?.inputs)],
      accountType,
    );

    // To check disable/default dialcode
    const phoneConfig = contactSectionConfig?.inputs?.find(
      (input: MyAccountInput) => input.fieldName === MyAccountFieldName.PHONE,
    );
    const phoneDefaultCountryFlag = getExt(MyAccountExtEnum.DEFAULT_COUNTRY_VALUE, phoneConfig?.ext ?? []) as boolean;
    const phoneDisabledCountry = getExt(MyAccountExtEnum.COUNTRY_NOT_EDITABLE, phoneConfig?.ext ?? []) as boolean;
    // <-- end

    // Setup DOB Year List
    const dobInput = infoSectionConfig?.inputs?.find(
      (input: MyAccountInput) => input.fieldName === MyAccountFieldName.BIRTHDATE,
    );
    const dobYearList = useMemo(() => {
      const currentYear = new Date().getFullYear();
      const minYear = currentYear - (dobInput?.validations?.maxAge ?? 100);
      const maxYear = currentYear - (dobInput?.validations?.minAge ?? 10);

      const yearList: DynamicObjectInterface[] = [];

      for (let yr = maxYear; yr >= minYear; yr--) {
        yearList.push({
          label: yr,
          value: yr,
        });
      }
      return yearList;
    }, [dobInput]);

    const dobMaxYear = useMemo(() => {
      const currentYear = new Date().getFullYear();
      return currentYear - (dobInput?.validations?.minAge ?? 10);
    }, [dobInput]);

    // Email & Phone flag for required OTP verification
    const [verificationType, setVerificationType] = useState<ProfilePhoneEmailEnum>();
    const [showOTPVerification, setShowOTPVerification] = useState<boolean>(false);
    const [challengeId, setChallengeId] = useState<string>('');
    const [profileInfo, setProfileInfo] = useState<ProfileInformationType>(ProfileInitialValue);
    const [isProfileChanged, setIsProfileChanged] = useState<boolean>(false);
    const [verificationValue, setVerificationValue] = useState<string>('');
    const [emailChallengeId, setEmailChallengeId] = useState<string>('');
    const [phoneChallengeId, setPhoneChallengeId] = useState<string>('');
    const [emailVerified, setEmailVerified] = useState<boolean>(false);
    const [phoneVerified, setPhoneVerified] = useState<boolean>(false);
    const [phoneInUsed, setPhoneInUsed] = useState<boolean>(false);
    const [emailInUsed, setEmailInUsed] = useState<boolean>(false);
    const [isPhoneInputValid, setIsPhoneInputValid] = useState<boolean>(false);
    const [apiError, setApiError] = useState<ApiErrorProps>({
      show: false,
      headline: '',
      body: '',
    });

    // Static Labels -->
    const lableUseAsBillingAddress = t('profile.ProfileInfo.useAsBillingAddress');
    const lableUseAsMailingAddress = t('profile.ProfileInfo.useAsMailingAddress');

    const labelEmail = t('profile.ProfileInfo.email');
    const labelCountry = t('profile.ProfileInfo.country');
    const labelPhone = t('profile.ProfileInfo.phone');
    const labelAddAltNumber = t('profile.ProfileInfo.addAlternativeNumber');

    const labelOTPVerifyRequired = t('profile.ProfileInfo.buttons.requireVerification');
    const labelMobileVerified = t('profile.ProfileInfo.buttons.mobileVerified');
    const labelEmailVerified = t('profile.ProfileInfo.buttons.emailVerified');

    const labelVerify = t('profile.ProfileInfo.buttons.verify');

    const saveSuccessLabel = t('profile.ProfileInfo.status.success');
    // <-- end of Static Labels

    useImperativeHandle(
      ref,
      () =>
        ({
          saveProfileHandler: handleProfileSave,
        }) as DynamicObjectInterface,
    );

    // Formik setup -->
    const formik = useFormik({
      initialValues: profileInfo.profileData,
      validationSchema,
      validateOnMount: validationOnMount,
      validateOnBlur: true,
      onSubmit: () => void 0,
      enableReinitialize: true,
    });
    // <-- end of Formik setup

    useEffect(() => {
      setConfig(masterDataConfig);
    }, []);

    useEffect(() => {
      // To handle drawer & OTP issue
      if (showOTPVerification && isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        // enable scroll
        document.body.style.removeProperty('overflow');
      }

      // Cleanup on unmount
      return () => {
        document.body.style.removeProperty('overflow');
      };
    }, [showOTPVerification]);

    useEffect(() => {
      if (isOTPVerified) {
        handleOTPVerified(verificationType);
      }
    }, [isOTPVerified, verificationType]);

    useEffect(() => {
      // To use local state instead of redux, so it will not affect the redux state value until save
      setProfileInfo({
        ...profileInformation,
        // to identify is billing address different from mailing address
        useAsBilling: useAsBillingConfig?.visible
          ? !isAddressModified(profileInformation?.mailingAddress, profileInformation?.billingAddress, addressFields)
          : useAsBillingConfig?.defaultValue,
        useAsMailing: useAsMailingConfig?.visible
          ? !isAddressModified(profileInformation?.mailingAddress, profileInformation?.billingAddress, addressFields)
          : useAsMailingConfig?.defaultValue,
      });
    }, [profileInformation]);

    useEffect(() => {
      if (phoneDefaultCountryFlag) {
        // Set default country code
        formik.setFieldValue?.(MyAccountFieldName.DIALCODE, defaultCountryDialCode);
      }
    }, [phoneDefaultCountryFlag]);

    useEffect(() => {
      validateData({
        _profileInfo: {
          ...profileInfo,
          profileData: {
            alternativePhone: profileInfo.profileData?.alternativePhone,
            ...formik.values,
          },
        },
        _originalProfile: originalProfile,
        _phoneVerified: phoneVerified,
        _emailVerified: emailVerified,
      });
    }, [profileInfo, formik.values, isProfileChanged, phoneVerified, emailVerified, originalProfile]);

    const validateData = async ({
      _profileInfo,
      _originalProfile,
      _phoneVerified,
      _emailVerified,
    }: DynamicObjectInterface) => {
      const isValid = await profileValidation({
        schema: validationSchema,
        addrSectionConfig: addrSectionConfig?.inputs as MyAccountInput[],
        profileInfo: _profileInfo,
        originalProfile: _originalProfile,
        phoneVerified: _phoneVerified,
        emailVerified: _emailVerified,
        addrMandatoryFields: addressMandatoryFields,
        isEmailRequired: isEmailRequired(),
      });

      // Address updated
      const { mailingAddress, billingAddress } = _profileInfo;
      const isAddressUpdated = mailingAddress?.updated || billingAddress?.updated;

      // Additional phone updated/deleted
      const isAltPhoneDeleted = _profileInfo.profileData?.alternativePhone?.some(
        (altPhone: AlternativeContactType) => altPhone?.isDeleted,
      );
      const updatedAltPhones = _profileInfo.profileData?.alternativePhone?.filter(
        (altPhone: AlternativeContactType) => altPhone?.isUpdated,
      );

      const isAltPhoneUpdatedValid = updatedAltPhones?.length
        ? updatedAltPhones.every((altPhone: AlternativeContactType) => altPhone?.isValid)
        : false;

      const allowSave = isValid && (formik.dirty || isAddressUpdated || isAltPhoneDeleted || isAltPhoneUpdatedValid);
      saveDisabledCallback(!allowSave);
    };

    // Handle profile updated
    const handleProfileSave = async () => {
      try {
        const profileInfoLocal = {
          ...profileInfo,
          profileData: {
            alternativePhone: profileInfo.profileData?.alternativePhone,
            ...formik.values,
            emailChallengeId: emailChallengeId,
            phoneChallengeId: phoneChallengeId,
          },
        };
        const sliceUpdateHandler =
          sliceName === SliceConfigName.PERSONAL_INFORMATION ? updateProfileInformationData : updateAccountInformation;
        await saveProfileDataHandler(
          profileInfoLocal,
          profileInformation,
          profileId,
          accountNumber,
          sliceUpdateHandler,
        );
        setEditingMode(ProfileInformationModeEnum.VIEW);
        addToast({
          id: ToastMessageEnum.INFORMATION_SAVED,
          timeout: 3000,
          content: <Notification hasNotificationIcon={true} theme={Theme.SUCCESS} message={saveSuccessLabel} />,
        });
      } catch (error: DynamicObjectInterface) {
        // Error handling
        const errorCode = error?.responseBody?.code ?? ErrorConstants.PROFILE_GENERAL_ERROR_CODE;
        switch (errorCode) {
          case ErrorConstants.EMAIL_NOT_UNIQUE:
          case ErrorConstants.ACCOUNT_EMAIL_NOT_UNIQUE:
            setEmailInUsed(true);
            break;
          case ErrorConstants.PHONE_NOT_UNIQUE:
          case ErrorConstants.ACCOUNT_PHONE_NOT_UNIQUE:
            setPhoneInUsed(true);
            break;

          default:
            logger.error(error);
            setApiError({
              show: true,
              headline: 'profile.ApiErrorModal.headline',
              body: `profile.ApiErrorModal.profile.${errorCode}`,
            });
        }
      }
    };

    // Address functions
    const contactTypeToUseAs: DynamicObjectInterface = {
      [ContactType.Mailing]: MyAccountFieldName.USE_AS_MAILING,
      [ContactType.Billing]: MyAccountFieldName.USE_AS_BILLING,
    };
    const contactTypeCombination: DynamicObjectInterface = {
      // Copy from & to
      [ContactType.Mailing]: [MyAccountFieldName.BILLING_ADDRESS, MyAccountFieldName.MAILING_ADDRESS],
      [ContactType.Billing]: [MyAccountFieldName.MAILING_ADDRESS, MyAccountFieldName.BILLING_ADDRESS],
    };
    const contactTypeToFieldName: DynamicObjectInterface = {
      [ContactType.Mailing]: MyAccountFieldName.MAILING_ADDRESS,
      [ContactType.Billing]: MyAccountFieldName.BILLING_ADDRESS,
    };
    const addressUseAsHandler = (checked: boolean, toType: ContactType = ContactType.Billing) => {
      const useAsFieldName = contactTypeToUseAs[toType];
      const addressFieldName = contactTypeToFieldName[toType];
      const [fromAddrName, toAddrName] = contactTypeCombination[toType];

      const emptyAddr = AddressInitialValue(toType, accountNumber, OwnerType.Account);
      setProfileInfo((prevProfile: ProfileInformationType) => {
        const newAddr = checked
          ? copyAddress(emptyAddr, prevProfile?.[fromAddrName], prevProfile?.[toAddrName])
          : prevProfile?.[toAddrName];

        const updatedProfile: ProfileInformationType = {
          ...prevProfile,
          [useAsFieldName]: checked,
          [addressFieldName]: newAddr,
        };
        return updatedProfile;
      });
    };
    const addressCallback = (addr: AddressContactType) => {
      const data: DynamicObjectInterface = {
        addr,
        contactTypeToFieldName,
        contactTypeCombination,
        profileInfo,
        useAsBillingConfig,
        useAsMailingConfig,
        accountNumber,
      };
      const handlers: DynamicObjectInterface = {
        setProfileInfo,
        setIsProfileChanged,
      };
      handleAddressCallback(data, handlers);
    };

    // OTP verification -->
    const handleOTPVerified = (type?: ProfilePhoneEmailEnum) => {
      if (type === ProfilePhoneEmailEnum.PHONE) {
        setPhoneVerified(true);
        setPhoneChallengeId(challengeId);
      } else {
        setEmailVerified(true);
        setEmailChallengeId(challengeId);
      }
    };

    const verifyContactCTA = (
      dataType: ProfilePhoneEmailEnum,
      requiredVerify: boolean,
      currentValue: string,
      currentDialCode?: string,
    ) =>
      handleVerifyContactCTA({
        data: {
          dataType,
          labelVerify,
          requiredVerify,
          currentValue,
          currentDialCode,
          profileId,
          accountNum: accountNumber,
          useAccountApiForContacts,
        },
        funcs: {
          setVerificationType,
          setVerificationValue,
          setShowOTPVerification,
          setChallengeId,
          setEmailInUsed,
          setPhoneInUsed,
        },
      });

    const contactVerificationCloseHandler = () => {
      setShowOTPVerification(false);
    };
    // <-- end of OTP verification

    // Alternative Contacts -->
    const alternativeContactCallback = (data: AlternativeContactCallbackProps) => {
      setProfileInfo((prevProfile: ProfileInformationType) => {
        const updatedAltContacts = processAltContact(data, prevProfile);

        return {
          ...prevProfile,
          profileData: {
            ...prevProfile.profileData,
            alternativePhone: updatedAltContacts,
          },
        };
      });

      setIsProfileChanged(true);
    };

    const addAlternativeContactHandler = (e: React.KeyboardEvent | React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // To update based on new schema and the new ID
      const newContact: AlternativeContactType = {
        ...AlternativeContactInitialValue,

        // generate random id to handle new record
        id: new Date().getTime(),
        isUpdated: false,
        isDeleted: false,
      };

      setProfileInfo((prevProfile) => {
        // Adding new contact
        const prevAlts = prevProfile.profileData.alternativePhone ?? [];
        const updatedAlternativeContact = [...prevAlts, newContact];

        return {
          ...prevProfile,
          profileData: {
            ...prevProfile.profileData,
            alternativePhone: updatedAlternativeContact,
          },
        };
      });
    };
    // <-- end of Alternative Contacts

    // Profile Utils -->
    const getClassName = (fillWidth: boolean) => ifThenElse(isMobile && fillWidth, styles['span-col'], '');
    const getIsIditable = (editable?: boolean, isEditableOnce?: boolean, oriValue?: DynamicObjectInterface) =>
      editable && ((isEditableOnce && !oriValue) || !isEditableOnce);
    const processEditableOnceMsg = (errMsg?: string, isEditableOnce?: boolean) => {
      if (!errMsg && isEditableOnce) {
        return 'profile.ProfileInfo.updateOneTime';
      }
      return errMsg;
    };
    // <-- end of Utils

    // Address Utils -->
    const addrStatus = (str?: string) => (str ? InputValidations.DEFAULT : InputValidations.INVALID);
    const addrError = (str?: string) => (str ? '' : 'profile.validationMsg.address.required');
    const isAddressVisibleEditable = (input: MyAccountInput) => {
      const { fieldName } = input;
      return (
        [MyAccountFieldName.MAILING_ADDRESS, MyAccountFieldName.BILLING_ADDRESS].includes(
          fieldName as MyAccountFieldName,
        ) && input?.visible
      );
    };
    const contactTypes: DynamicObjectInterface = {
      mailingAddress: ContactType.Mailing,
      billingAddress: ContactType.Billing,
    };
    // <-- end of Address Utils

    // Field Rendering -->
    const renderTextInput = (fieldName: string, otherData?: DynamicObjectInterface) => {
      const value = formik.values?.[fieldName];
      const { label, isTouched, showAsInput, editable, isEditableOnce, required, max, min } = otherData;
      const labelParams = { max, min };

      const labelTxt = processLabelText(t, label, required);

      if (editable) {
        const meta = formik.getFieldMeta(fieldName);
        const errMsg = processEditableOnceMsg(meta.error, isEditableOnce);
        return (
          <Input
            value={value}
            label={labelTxt}
            id={fieldName}
            name={fieldName}
            onChange={formik.handleChange}
            // required={required} // Temporary comment this due to framework will render *
            onBlur={formik.handleBlur}
            status={inputErrorStatus(isTouched, meta.error)}
            data-testid={`${fieldName}-testid`}
            message={inputErrorMessage(t, isTouched, errMsg, labelParams)}
          />
        );
      } else {
        let viewLabelTxt = label;
        if (fieldName === MyAccountFieldName.ID_NUMBER && profileInfo?.citizenship !== Citizenship.Citizen) {
          viewLabelTxt = t(`profile.ProfileInfo.idNumber.foreigner`);
        }

        return <DisplayField label={viewLabelTxt} fieldName={fieldName} value={value} showAsInput={showAsInput} />;
      }
    };

    const renderDateInput = (fieldName: string, otherData?: DynamicObjectInterface) => {
      const value = formik.values?.[fieldName];
      const { label, isTouched, showAsInput, editable, isEditableOnce, required, minAge } = otherData;
      const labelParams = { min: minAge };

      if (editable) {
        const meta = formik.getFieldMeta(fieldName);
        const {
          birthDate,
          formattedDate,
          value: finalValue,
          classNames,
        } = processDateField(styles, value, dobMaxYear, dateLocale);

        const labelTxt = processLabelText(t, label, required);
        const errMsg = processEditableOnceMsg(meta.error, isEditableOnce);
        return (
          <>
            <div className={`${classNames}`}>
              {finalValue && <div className={styles['custom-label']}>{label}</div>}
              <DatePicker
                displayText={formattedDate}
                inputContainerHeight="56px"
                showFullCalendar={true}
                onChange={async (date?: Date) => await onChangeDateHandler(date)}
                value={finalValue}
                placeholder={labelTxt}
                calendarHeading={labelTxt}
                onCalendarToggle={() => {
                  formik.setFieldTouched(fieldName);
                }}
                data-testid={`${fieldName}-testid`}
                timeLabel={''}
                timeLabelStart={''}
                timeLabelEnd={''}
                scheduleLabel={t('profile.ProfileInfo.buttons.save')}
                cancelLabel={t('profile.ProfileInfo.buttons.cancel')}
                status={inputErrorStatus(isTouched, meta.error)}
                yearDropdownConfigData={{
                  label: 'value',
                  onSelect: async (item: DynamicObjectInterface) => {
                    const newDate = new Date(birthDate);
                    newDate.setFullYear(item.value);
                    await onChangeDateHandler(newDate);
                  },
                }}
                showYearDropdown={CalendarYearDropdownType.CUSTOM}
                yearDropdownData={dobYearList}
              />
            </div>
            {/* use custom error component while waiting framework team to update the datePicker message color */}
            {InputErrorComponent({
              t,
              ...meta,
              error: errMsg,
              touched: isTouched,
              labelParams: labelParams,
            })}
          </>
        );
      } else {
        return (
          <DisplayField
            fieldName={fieldName}
            value={value}
            type={MyAccountFieldType.DATE}
            label={label}
            showAsInput={showAsInput}
            dateLocale={dateLocale}
          />
        );
      }
    };

    const renderDropdownInput = (fieldName: string, otherData?: DynamicObjectInterface) => {
      const value = formik.values?.[fieldName];
      const { label, isTouched, showAsInput, editable, isEditableOnce, required, options, ext } = otherData;
      const labelTxt = processLabelText(t, label, required);
      let dispValue = getDropdownValueByOptions(t, fieldName, value, options as unknown as Data[]);

      if (editable) {
        const meta = formik.getFieldMeta(fieldName);
        const dropDownData = generateDropdownListByOptions(options, t, fieldName);
        const dispValue = getDropdownValueByOptions(t, fieldName, value, options);

        let errMsg = meta.error;
        if (required) {
          errMsg = conditionalError(meta.error, `profile.validationMsg.${fieldName}.required`);
        }
        errMsg = processEditableOnceMsg(errMsg, isEditableOnce);

        return (
          <DropDown
            value={dispValue}
            dropdownLabel={labelTxt}
            name={fieldName}
            id={fieldName}
            data={dropDownData as DynamicObjectInterface[]}
            configData={dropdownConfig(fieldName)}
            disabledFilter={true}
            onBlur={formik.handleBlur}
            data-testid={`${fieldName}-testid`}
            status={inputErrorStatus(isTouched, meta.error)}
            message={inputErrorMessage(t, isTouched, errMsg)}
          />
        );
      } else {
        if (fieldName === MyAccountFieldName.CITIZENSHIP) {
          dispValue = getCitizenshipCountryLabel(fieldName, value, options, ext);
        }
        // Temporary handling for documentType while waiting update from masterdata
        if (fieldName === MyAccountFieldName.DOCUMENT_TYPE) {
          dispValue = t(`profile.ProfileInfo.${fieldName}.${formik.values?.documentType}`);
        }

        return <DisplayField fieldName={fieldName} value={dispValue} label={label} showAsInput={showAsInput} />;
      }
    };

    const getCitizenshipCountryLabel = (
      fieldName: string,
      value: string,
      options: DynamicObjectInterface,
      ext: DynamicObjectInterface,
    ) => {
      let dispValue = getDropdownValueByOptions(t, fieldName, value, options, siteCountryCode?.toUpperCase());

      if (profileInfo?.profileData?.[fieldName] !== Citizenship.Citizen) {
        // use custom field name for citizenship
        const citizenshipCountryField = String(getExt(MyAccountExtEnum.DISPLAY_CITIZENSHIP_COUNTRY_FIELD, ext));
        const citizenshipValue = profileInfo?.profileData?.[citizenshipCountryField];
        if (citizenshipCountryField && citizenshipValue) {
          // override display value if citizenship country field is defined in masterdata
          dispValue = t(`profile.RegistrationIdVerificationStep.country.${citizenshipValue}`);
        }
      }
      return dispValue;
    };

    const renderInformations = (inputs?: MyAccountInput[]) => {
      const renderInputs: JSX.Element[] = [];

      inputs
        ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
        ?.forEach((input: MyAccountInput) => {
          const { fieldName, options, validations = {}, editable, isEditableOnce, ext } = input;
          const { required, max: maxValue, min: minValue, minAge } = validations;
          const min = parseString(minValue);
          const max = parseString(maxValue);

          const fullWidth = getExt(MyAccountExtEnum.UI_FULL_WIDTH, ext) as boolean;
          const showAsInput = getExt(MyAccountExtEnum.DISPLAY_AS_INPUT, ext) as boolean;

          const isTouched = isFieldTouched(validationOnMount, formik.getFieldMeta(fieldName)?.touched, isEditableOnce);
          const fieldType = fieldTypeByInput(input);

          const oriValue = profileInformation.profileData?.[fieldName];
          const isEditable = getIsIditable(editable, isEditableOnce, oriValue);
          const divClassName = isEditable ? getClassName(fullWidth) : '';

          const label = getLabel(input);

          switch (fieldType) {
            case MyAccountFieldType.TEXT: {
              // Text input
              const textInput = renderTextInput(fieldName, {
                label,
                isTouched,
                showAsInput,
                editable: isEditable,
                isEditableOnce,
                required,
                max,
                min,
              });
              renderInputs.push(
                <div key={fieldName} className={divClassName}>
                  {textInput}
                </div>,
              );
              break;
            }

            case MyAccountFieldType.DATE: {
              // Date Input

              let dateLabel = label;
              const { citizenship } = formik.values;
              if (citizenship !== Citizenship.Citizen) {
                dateLabel = t(`profile.ProfileInfo.${fieldName}`);
              }

              const dateInput = renderDateInput(fieldName, {
                label: dateLabel,
                isTouched,
                showAsInput,
                editable: isEditable,
                isEditableOnce,
                required,
                minAge,
                ext,
              });
              renderInputs.push(
                <div key={fieldName} className={divClassName}>
                  {dateInput}
                </div>,
              );
              break;
            }

            case MyAccountFieldType.DROPDOWN: {
              // Dropdown input
              const dropdownInput = renderDropdownInput(fieldName, {
                label,
                isTouched,
                showAsInput,
                editable: isEditable,
                isEditableOnce,
                required,
                options,
                ext,
              });
              renderInputs.push(
                <div key={fieldName} className={divClassName}>
                  {dropdownInput}
                </div>,
              );
              break;
            }

            case MyAccountFieldType.PICTURE: {
              renderInputs.push(
                <div className={`${styles['span-col']} ${styles['profile-picture-container']}`}>
                  <div className={styles['image-wrapper']}>
                    <FAIcon
                      iconName="circle-user"
                      className={styles['profile-picture']}
                      iconStyle={IconStyle.SOLID}
                      theme={Theme.PURPLE}
                    />
                  </div>
                </div>,
              );
              break;
            }

            default:
          }
        });
      return renderInputs;
    };

    const renderAddressInput = (input: MyAccountInput, profileInfoData?: ProfileInformationType, isFirst?: boolean) => {
      const { fieldName, editable } = input;
      const output: JSX.Element[] = [];
      let visible = false;

      const addrValue = profileInfoData?.[fieldName];
      const formattedAddr = addrFormat(addrValue, true);
      const contactType = contactTypes[fieldName];
      const addressValidationStatus = addrStatus(addrValue?.addrLineOne);
      const addressValidationMessage = addrError(addrValue?.addrLineOne);
      const label = getLabel(input, true);

      const addressInput = editable ? (
        <AddressInput
          contactType={contactType}
          address={addrValue}
          callback={addressCallback}
          ownerType={OwnerType.Account}
          accountNumber={accountNumber}
          useMobileView={false}
          status={addressValidationStatus}
          message={addressValidationMessage}
          isMandatory={true}
          validateOnMount={validationOnMount}
        />
      ) : (
        <DisplayField label={label} value={formattedAddr} />
      );

      if (isFirst) {
        output.push(addressInput);
        visible = true;
      } else if (
        (fieldName === MyAccountFieldName.BILLING_ADDRESS &&
          useAsBillingConfig?.visible &&
          !profileInfo.useAsBilling) ||
        (fieldName === MyAccountFieldName.MAILING_ADDRESS && useAsMailingConfig?.visible && !profileInfo.useAsMailing)
      ) {
        output.push(<div className={styles['divider']}></div>);
        output.push(addressInput);
        visible = true;
      }

      if (isFirst) {
        // Render checkbox
        if (
          fieldName === MyAccountFieldName.MAILING_ADDRESS &&
          useAsBillingConfig?.visible &&
          useAsBillingConfig?.editable
        ) {
          output.push(
            <div className={styles['address-checkbox']}>
              <Checkbox
                data-testid="use-as-billing-address-checkbox-testid"
                text={lableUseAsBillingAddress}
                checked={profileInfo.useAsBilling}
                onChange={() => addressUseAsHandler(!profileInfo.useAsBilling)}
              />
            </div>,
          );
        } else if (
          fieldName === MyAccountFieldName.BILLING_ADDRESS &&
          useAsMailingConfig?.visible &&
          useAsMailingConfig?.editable
        ) {
          output.push(
            <div className={styles['address-checkbox']}>
              <Checkbox
                data-testid="use-as-mailing-address-checkbox-testid"
                text={lableUseAsMailingAddress}
                checked={profileInfo.useAsMailing}
                onChange={() => addressUseAsHandler(!profileInfo.useAsMailing, ContactType.Mailing)}
              />
            </div>,
          );
        }
      }

      return { output, visible };
    };

    const renderAddresses = (inputs?: MyAccountInput[], profileInfoData?: ProfileInformationType) => {
      const renderInputs: JSX.Element[] = [];

      let isFirst = true;

      inputs
        ?.filter((inputItem: MyAccountInput) => isAddressVisibleEditable(inputItem))
        .forEach((input: MyAccountInput) => {
          const { output } = renderAddressInput(input, profileInfoData, isFirst);
          isFirst = false;

          renderInputs.push(
            <div className={styles['address-section']} data-testid={`profile-address-${input.fieldName}-testid`}>
              {output}
            </div>,
          );
        });

      return renderInputs;
    };

    const renderContactEmail = (input: MyAccountInput) => {
      const { fieldName, validations = {} } = input;
      const value = formik.values?.[fieldName];
      const meta = formik.getFieldMeta(fieldName);

      const data: ProfileEmailProcessType = {
        emailVerified,
        isEmailInUsed: emailInUsed,
        email: parseString(value),
        originalEmail: originalProfile.email,
      };

      const { isEmailValid, emailRequireOTP, className, errMsg, forceError } = processEmailField(meta, styles, data);

      const isTouched = isFieldTouched(validationOnMount, meta.touched) || forceError;

      const labelTxt = processLabelText(t, labelEmail, validations?.required);
      return (
        <div className={`${styles['contact-input']} ${className}`}>
          <Input
            value={value}
            name={MyAccountFieldName.EMAIL}
            label={labelTxt}
            type="email"
            onChange={onChangeEmailHandler}
            onBlur={formik.handleBlur}
            // required={validations?.required} // Temporary comment this due to framework will render *
            autoComplete={'off'}
            data-testid="profile-contact-email-testid"
            ctaTextLink={verifyContactCTA(ProfilePhoneEmailEnum.EMAIL, emailRequireOTP, parseString(value))}
            status={inputErrorStatus(isTouched, errMsg)}
            message={inputErrorMessage(t, isTouched, errMsg)}
          />
          {isEmailValid && emailRequireOTP && !emailInUsed && (
            <div className={styles['opt-verification']}>{labelOTPVerifyRequired}</div>
          )}
          {isEmailValid && emailVerified && (
            <div className={styles['opt-verified']}>
              <FAIcon iconName="circle-check" theme={Theme.SUCCESS} /> {labelEmailVerified}
            </div>
          )}
        </div>
      );
    };

    const handlePhoneValidChange = (isValid: boolean, fieldName: string) => {
      setIsPhoneInputValid(isValid);

      if (!isValid) {
        formik.setFieldError(fieldName, t('profile.validationMsg.phone.format'));
      } else {
        formik.setFieldError(fieldName, '');
      }
    };

    const renderContactPhone = (input: MyAccountInput) => {
      const { fieldName, validations = {} } = input;
      const meta = formik.getFieldMeta(fieldName);

      const { phone: phoneValue, dialCode: dialCodeValue } = formik.values;
      const phone = parseString(phoneValue);
      const dialCode = parseString(dialCodeValue);

      const countryLabelTxt = ifThenElse(phoneDisabledCountry, '', labelCountry);

      let countryDialCode = dialCode;
      if (phoneDefaultCountryFlag || !countryDialCode) {
        countryDialCode = defaultCountryDialCode;
      }

      const data: ProfilePhoneProcessType = {
        phoneVerified,
        isPhoneInUsed: phoneInUsed,
        phone,
        dialCode: countryDialCode,
        originalPhone: originalProfile.phone,
        originalDialCode: originalProfile.dialCode,
        isPhoneInputValid,
      };
      const { className, phoneRequireOTP, errMsg, isPhoneValid, forceError } = processPhoneField(meta, styles, data);
      const isTouched = validationOnMount || meta.touched || forceError;

      const labelTxt = processLabelText(t, labelPhone, validations?.required);
      return (
        <div className={`${styles['contact-input']} ${className}`}>
          <PhoneNumberInput
            name={MyAccountFieldName.PHONE}
            data-testid="profile-contact-phone-testid"
            dropdownLabel={countryLabelTxt}
            dropdownValue={countryDialCode}
            dropdownDisabled={phoneDisabledCountry}
            disabledFilter={true}
            configData={{
              label: 'name',
              onSelect: (e: DynamicObjectInterface) => {
                formik.setFieldValue?.(MyAccountFieldName.DIALCODE, e.name);
                setPhoneInUsed(false);
              },
            }}
            inputLabel={labelTxt}
            inputValue={phone}
            onInputChange={(number?: string) => onChangePhoneHandler(fieldName, number)}
            onKeyUp={onKeyUpPhoneHandler}
            onBlur={formik.handleBlur}
            onBlurCapture={formik.handleBlur}
            autoComplete={'off'}
            ctaTextLink={verifyContactCTA(ProfilePhoneEmailEnum.PHONE, phoneRequireOTP, phone, countryDialCode)}
            meta={meta}
            isAutoValidate={!!phone && !!countryDialCode && !phoneInUsed && meta.touched}
            isPhoneNumberValid={(isValid: boolean) => handlePhoneValidChange(isValid, fieldName)}
            validationStatus={inputErrorStatus(isTouched, errMsg)}
            validationMessage={inputErrorMessage(t, isTouched, errMsg)}
          />
          {isPhoneValid && phoneRequireOTP && !phoneInUsed && (
            <div className={styles['opt-verification']}>{labelOTPVerifyRequired}</div>
          )}
          {isPhoneValid && phoneVerified && (
            <div className={styles['opt-verified']}>
              <FAIcon iconName="circle-check" theme={Theme.SUCCESS} /> {labelMobileVerified}
            </div>
          )}
        </div>
      );
    };

    const renderContacts = (inputs?: MyAccountInput[]) => {
      const renderInputs: JSX.Element[] = [];

      inputs
        ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
        ?.forEach((input: MyAccountInput) => {
          const { fieldName } = input;

          switch (fieldName) {
            case MyAccountFieldName.EMAIL:
              renderInputs.push(renderContactEmail(input));
              break;
            case MyAccountFieldName.PHONE:
              renderInputs.push(renderContactPhone(input));
              break;

            default: {
              // do nothing
            }
          }
        });
      return renderInputs;
    };

    const renderTax = (inputs?: MyAccountInput[], profileInfoData?: ProfileInformationType) => {
      // Currently this is non-editable
      const renderInputs: JSX.Element[] = [];

      inputs
        ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
        ?.forEach((input: MyAccountInput) => {
          const { fieldName, fieldType, options } = input;
          let value = parseString(profileInfoData?.profileData?.[fieldName]);
          let label = getLabel(input);

          if (fieldName === MyAccountFieldName.TAX_ADDRESS) {
            value = addrFormat(profileInfoData?.[fieldName], true);
            label = t(`profile.AddressInput.${fieldName}`);
          } else if (fieldType === MyAccountFieldType.DROPDOWN) {
            value = getDropdownValueByOptions(t, fieldName, value, options as unknown as Data[]);
          }

          renderInputs.push(<DisplayField label={label} value={value} />);
        });

      return renderInputs;
    };

    // Handlers -->
    const onChangeEmailHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.handleChange(e);

      // Reset contact in-use message
      setEmailInUsed(false);
      setEmailVerified(false);
    };

    const onChangePhoneHandler = (fieldName: string, value?: string) => {
      formik.handleChange({ target: { name: fieldName, value } });
    };

    const onKeyUpPhoneHandler = () => {
      // Reset contact in-use message
      setPhoneInUsed(false);
      setPhoneVerified(false);
    };
    const onChangeDateHandler = async (date?: Date) => {
      const year = date?.getFullYear();
      const month = String(parseNumber(date?.getMonth()) + 1).padStart(2, '0'); // January is 0!
      const day = String(date?.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      await formik.setFieldValue(MyAccountFieldName.BIRTHDATE, formattedDate);
    };

    const dropdownConfig = (fieldId: string) => ({
      label: 'label',
      onSelect: (item: DynamicObjectInterface) => {
        formik.setFieldValue(fieldId, item.value);
      },
    });

    const closeErrorModalHandler = () => {
      setApiError({ show: false, headline: '', body: '' });
    };

    return (
      <>
        <section className={styles['profile-info-container']} data-testid="profile-info-form-container-testid">
          <div className={styles['form']}>
            {configList?.map((sectionItem: MyAccountInput) => {
              const { section, inputs, ext } = sectionItem;
              if (section === ProfileSectionConst.PROFILE_SECTION_ADDRESS) {
                const renderInputs = renderAddresses(inputs, profileInfo);

                const removeBorderConfig = (getExt(MyAccountExtEnum.UI_REMOVE_BORDER_BOTTOM, ext) as string[]) ?? [];
                const removeBorderBottom = removeBorderConfig.includes(breakpoint);
                const borderClassName = ifThenElse(removeBorderBottom, styles['remove-border-bottom'], '');

                return (
                  <section
                    key={section}
                    className={`${styles['field-section']} ${styles['address']} ${borderClassName}`}
                    data-testid="profile-mailing-section-testid"
                  >
                    {renderInputs}
                  </section>
                );
              } else if (
                section === ProfileSectionConst.PROFILE_SECTION_TAX &&
                profileInformation?.profileData?.citizenship === Citizenship.Citizen
              ) {
                const renderInputs = renderTax(inputs, profileInformation);
                const columns = (getExt(MyAccountExtEnum.UI_COLUMNS, ext) as number) ?? 0;
                const columnClass = ifThenElse(columns >= 2, styles['two-cols'], '');

                return (
                  <section
                    key={section}
                    className={`${styles['field-section']} ${styles['tax']} ${columnClass}`}
                    data-testid="profile-mailing-section-testid"
                  >
                    <div className={styles['tax-section']} data-testid="profile-tax-testid">
                      {renderInputs}
                    </div>
                  </section>
                );
              } else if (section === ProfileSectionConst.PROFILE_SECTION_INFO) {
                const renderInputs = renderInformations(inputs);
                const columns = (getExt(MyAccountExtEnum.UI_COLUMNS, sectionItem.ext) as number) ?? 0;
                const columnClass = ifThenElse(columns >= 2, styles['two-cols'], '');

                return (
                  <section
                    key={section}
                    className={`${styles['field-section']} ${styles['information']} ${columnClass}`}
                    data-testid="profile-info-section-testid"
                  >
                    {renderInputs}
                  </section>
                );
              } else if (section === ProfileSectionConst.PROFILE_SECTION_CONTACT) {
                const renderInputs = renderContacts(inputs);

                return (
                  <section key={section} className={`${styles['field-section']} ${styles['contact']}`}>
                    <div className={styles['core-contacts']}>{renderInputs}</div>

                    {isAdditionalPhoneEnabled() && profileInfo.profileData.alternativePhone && (
                      <div className={styles['alt-contacts']}>
                        {profileInfo.profileData.alternativePhone
                          ?.filter(({ isDeleted }: AlternativeContactType) => !isDeleted)
                          ?.map((contact: AlternativeContactType, idx: number) => (
                            <AlternativeContact
                              altNum={idx + 1}
                              fieldId="alternative-phone"
                              contact={contact}
                              callback={alternativeContactCallback}
                              key={contact.id}
                            />
                          ))}
                      </div>
                    )}
                    {isAdditionalPhoneEnabled() && (
                      <div className={styles['alt-contacts-add']}>
                        <TextLink
                          data-testid="add-alt-phone-testid"
                          text={labelAddAltNumber}
                          textLinkStyle={TextLinkStyle.ICON}
                          leftIcon={{
                            iconName: 'plus',
                            iconStyle: IconStyle.SOLID,
                          }}
                          theme={Theme.PURPLE}
                          underline={TextLinkUnderlineStyle.NOHOVER}
                          onClick={addAlternativeContactHandler}
                        />
                      </div>
                    )}
                  </section>
                );
              }
              return <></>;
            })}
          </div>
        </section>

        {showOTPVerification && challengeId && (
          <OTPComponent
            challengeId={challengeId}
            username={verificationValue}
            onClose={contactVerificationCloseHandler}
            autoverify={true}
          />
        )}
        {apiError.show && (
          <ApiErrorModal
            {...apiError}
            primaryButtonCallback={closeErrorModalHandler}
            fallbackBody="profile.ApiErrorModal.profile.validation-error"
          />
        )}
      </>
    );
  },
);
AccountInformationEdit.displayName = 'AccountInformationEdit';
export default AccountInformationEdit;
